import { useCallback } from 'react';
import { Upload } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UploadZoneProps {
  onFileSelect: (file: File) => void;
  accept: string;
  maxSize: number;
  label: string;
  inputRef: React.RefObject<HTMLInputElement>;
  isDragActive?: boolean;
}

export function UploadZone({ 
  onFileSelect, 
  accept, 
  maxSize,
  label,
  inputRef,
  isDragActive 
}: UploadZoneProps) {
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) onFileSelect(file);
  }, [onFileSelect]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(file);
  }, [onFileSelect]);

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      className={cn(
        "relative border-2 border-dashed rounded-lg transition-all cursor-pointer",
        "hover:border-primary/50 hover:bg-gray-50",
        isDragActive ? "border-primary bg-primary/5" : "border-gray-200 bg-white",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      )}
    >
      <div className="px-6 py-8">
        <div className="flex flex-col items-center gap-3">
          <div className={cn(
            "p-3 rounded-full transition-colors",
            isDragActive ? "bg-primary/10" : "bg-gray-100"
          )}>
            <Upload className={cn(
              "h-6 w-6",
              isDragActive ? "text-primary" : "text-gray-400"
            )} />
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-600">
              {isDragActive ? 'أفلت الملف هنا' : (
                <>
                  <span className="text-primary">اضغط للاختيار</span>
                  {' أو اسحب وأفلت '}
                  {label}
                </>
              )}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              الحد الأقصى للحجم: {Math.round(maxSize / (1024 * 1024))}MB
            </p>
          </div>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileInput}
        className="hidden"
      />
    </div>
  );
}